import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Form, FormikProvider, useFormik } from 'formik';
import { Card, Button, CircularProgress, useTheme } from '@mui/material';
import * as Sentry from '@sentry/react';
import { AlertMessage } from '@eposnow/ui-core';
import BusinessInformationForm from './BusinessInformationForm';
import validationSchema from './validationSchema';
import { StepContext } from '../../context/StepContext';
import {
  MANDATORY_RESTRICTED_INDUSTRY_TYPES,
  OPTIONAL_RESTRICTED_INDUSTRY_TYPES,
} from '../../constants';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import ActionsContainer from '../../components/ActionsContainer';
import { BusinessInfo } from '../../types';
import { API_V2_HOSTNAME_KEY } from '../../localStorageKeys';
import { ApiContext, ErrorObject } from '../../context/ApiContext';
import Loader from '../../components/Loader';
import { checkAndFormatCountry, formatDateWithLeadingZeroes } from '../../helpers/helpers';
import { UserContext } from '../../context/UserContext';
import useCountries from '../../helpers/useCountries';
import useBusinessInformation from '../../helperHooks/useBusinessInformation';
import usePageTitle from '../../helperHooks/usePageTitle';
import ErrorLoadingData from '../../components/ErrorLoadingData';

const BusinessInformation = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { apiFetch } = useContext(ApiContext);
  const { goToNextStep, goToPreviousStep } = useContext(StepContext);
  const { locale } = useContext(UserContext);
  const { countries } = useCountries();
  const [errorMessage, setErrorMessage] = useState('');
  const { businessInformation, isLoading, isError, refetchBusinessInformation } =
    useBusinessInformation();

  usePageTitle('nav.businessInformation');

  const { isLoading: isUpdatingBusinessInfo, mutate: updateBusinessInfo } = useMutation(
    (newBusinessInfo: BusinessInfo) =>
      apiFetch(
        `${localStorage.getItem(API_V2_HOSTNAME_KEY)}onboarding/business-info`,
        newBusinessInfo,
        'PUT'
      ),
    {
      onSuccess: () => {
        goToNextStep();
      },
      onError(error: ErrorObject) {
        if (error?.code === 401) return;
        Sentry.captureException(new Error('Error updating business information'), {
          extra: { error: JSON.stringify(error) },
        });
        setErrorMessage(t('errors.errorSavingData'));
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      organisationName: businessInformation?.organisationName || '',
      industryType:
        typeof businessInformation?.industryType === 'number' &&
        businessInformation?.industryType > 0
          ? businessInformation?.industryType?.toString()
          : '',
      businessType:
        typeof businessInformation?.businessType === 'number' &&
        businessInformation?.businessType > 0
          ? businessInformation?.businessType?.toString()
          : '',
      proofOfLicenseNeeded: false,
      startTradingDate: businessInformation?.startTradingDate || '',
      phoneNumber: businessInformation?.contactNumber || '',
      addressLine1: businessInformation?.addressLine1 || '',
      town: businessInformation?.town || '',
      county: businessInformation?.county || '',
      postCode: businessInformation?.postCode || '',
      country: businessInformation?.country || '',
      trust: businessInformation?.trust,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      let requestValues = values;

      if (MANDATORY_RESTRICTED_INDUSTRY_TYPES?.includes(parseInt(values?.industryType, 10))) {
        requestValues = { ...values, proofOfLicenseNeeded: true };
      } else if (OPTIONAL_RESTRICTED_INDUSTRY_TYPES?.includes(parseInt(values?.industryType, 10))) {
        requestValues = { ...values, proofOfLicenseNeeded: values?.proofOfLicenseNeeded };
      } else {
        requestValues = { ...values, proofOfLicenseNeeded: false };
      }

      // To format date to be yyyy-mm-dd (format expected by the API)
      const startTradingDate = new Date(values?.startTradingDate);

      if (parseInt(requestValues.businessType, 10) !== 10) {
        requestValues.trust = undefined;
      } else if (requestValues.trust) {
        if (requestValues.trust?.trusteeType === 1) {
          requestValues.trust.trusteeLegalName = undefined;
        } else if (requestValues.trust?.trusteeType === 2) {
          requestValues.trust.trusteeFirstName = undefined;
          requestValues.trust.trusteeLastName = undefined;
        }
      }

      updateBusinessInfo({
        ...requestValues,
        industryType: parseInt(values?.industryType, 10),
        businessType: parseInt(values?.businessType, 10),
        startTradingDate: formatDateWithLeadingZeroes(startTradingDate),
        contactNumber: values?.phoneNumber,
        id: businessInformation?.id,
        country: checkAndFormatCountry(values.country, countries),
      });
    },
  });

  return (
    <>
      <PageHeader />

      {isLoading ? (
        <Loader ariaBusy={isLoading} />
      ) : (
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            {errorMessage && (
              <AlertMessage
                type="error"
                styles={{ marginBottom: 2 }}
                action={() => setErrorMessage('')}
                iconAlignment="baseline"
                locale={locale}
                theme={theme}
              >
                {errorMessage}
              </AlertMessage>
            )}

            <Card
              variant="outlined"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '1.5rem',
                marginBottom: '3rem',
              }}
            >
              {isError ? (
                <ErrorLoadingData action={refetchBusinessInformation} />
              ) : (
                <BusinessInformationForm formik={formik} />
              )}
            </Card>
            <ActionsContainer>
              <Button
                variant="outlined"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                onClick={() => {
                  goToPreviousStep();
                }}
              >
                {t('actions.previous')}
              </Button>
              <Button
                variant="contained"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                type="submit"
                disabled={isUpdatingBusinessInfo}
              >
                {isUpdatingBusinessInfo && (
                  <>
                    <CircularProgress color="inherit" size="20px" sx={{ marginRight: '8px' }} />
                    {t('actions.saving')}
                  </>
                )}
                {!isUpdatingBusinessInfo && t('actions.next')}
              </Button>
            </ActionsContainer>
          </Form>
        </FormikProvider>
      )}
    </>
  );
};

export default BusinessInformation;
